<template>
  <div>
    <aha-section>
      <template #header>
        <h5 v-if="!data.oneTime">
          {{ $i18nPresenter.t('Current plan') }}
        </h5>
        <h5 v-else>
          {{ $i18nPresenter.t('On-going plan') }}
        </h5>
      </template>
      <template #body>
        <h2 class="font-bold text-2xl mb-2">
          {{ subscriptionPlanName }}
          <span
            v-if="currentUser.id && plan && plan.group === 'plus' &&
              data.subscription && data.subscription.current_period_start"
          >
            ({{ $i18nPresenter.t('Legacy Plan') }})
            <span class="relative legacy-plus-infor">
              <i class="fas fa-info-circle" @mouseover="trackingPlusLegacy()"></i>
              <div class="cta-wrapper">
                <div
                  class="cta-upgrade tooltip-audio-upgrade-now"
                >
                  <div class="mb-2"><strong>{{ $i18nPresenter.t('Legacy Plan') }}</strong></div>
                  <div>
                    {{ $i18nPresenter.t("You are currently on a legacy plan. It is not shown in our Pricing Table.") }}
                  </div>
                  <aha-button class="button-upgrade-now" variant="base" @click="goToPostLegacy()">
                    {{ $i18nPresenter.t('Learn more') }} <i class="fas fa-external-link-alt ml-2"></i>
                  </aha-button>
                </div>
              </div>
              <div class="upgrade-arrow upgrade-arrow_slide"></div>
            </span>
          </span>
        </h2>
        <table class="table-plan mb-4">
          <colgroup>
            <col class="w-1/2 md:w-1/4">
            <col class="w-1/2 md:w-3/4">
          </colgroup>
          <tbody class="w-full">
            <tr>
              <td class="table-plan-property">
                <i class="mr-1 fas fa-user fa-fw" />
                {{ $i18nPresenter.t('Owner:') }}
              </td>
              <td class="py-2 table-plan-value">
                {{ currentUser.email }}
              </td>
            </tr>
            <tr>
              <td class="table-plan-property">
                <i class="mr-1 fas fa-users fa-fw" />
                {{ $i18nPresenter.t('Participant limit:') }}
              </td>
              <td class="py-2 table-plan-value">
                {{ participantLimit }}
              </td>
            </tr>
            <tr v-if="data.subscription && data.subscription.current_period_start">
              <td class="table-plan-property">
                <i class="mr-1 far fa-calendar-alt fa-fw" />
                {{ $i18nPresenter.t('Start date:') }}
              </td>
              <td class="py-2 table-plan-value">
                {{ periodStart | dateTimeStamp }}
              </td>
            </tr>
            <tr v-if=" data.subscription">
              <td class="table-plan-property">
                <i class="mr-1 far fa-calendar-check fa-fw" />
                <span v-if="hasNextBillingDate">
                  {{ $i18nPresenter.t('Next billing date:') }}
                </span>
                <span v-else>
                  {{ $i18nPresenter.t('End date:') }}
                </span>
              </td>
              <td class="py-2 table-plan-value">
                {{ periodEnd | dateTimeStamp }}
              </td>
            </tr>
            <tr
              v-if="isBulkLicenseSubscription && $store.state.plan.type != 'white-label'
                && data.subscription.bulkLicensesTotalAmount"
            >
              <td class="capitalize table-plan-property">
                <i class="mr-1 fas fa-money-check-alt fa-fw" />
                {{ $i18nPresenter.t(`${data.subscription.plan.billingCycle} cost:`) }}
              </td>

              <td class="py-2 table-plan-value">
                <b>{{ data.subscription.bulkLicensesTotalAmount | formatNumber }} {{ getCurrency }}</b> ({{ data.subscription.quantity }} licenses)
              </td>
            </tr>
            <tr
              v-if="!isBulkLicenseSubscription && data.subscription && $store.state.plan.type != 'white-label'
                && data.subscription.amount"
            >
              <td class="capitalize table-plan-property">
                <i class="mr-1 fas fa-money-check-alt fa-fw" />
                {{ $i18nPresenter.t(`${data.subscription.plan.billingCycle} cost:`) }}
              </td>
              <td class="py-2 table-plan-value" v-if="isDisplayingNextInvoicePromotion">
                <span
                  class="pr-1 line-through sub-has-coupon text-base-60"
                >{{ data.subscription.amount }} {{ isUserVietNam ? 'k':'' }} {{ getCurrency }}</span>
                <span>{{ promotionOnCancelAmount }} {{ getCurrency }}</span>
                <span
                  v-if="isDisplayingNextInvoicePromotion"
                >
                  <aha-badge
                    :label="`${promotionOnCancelPercent}% OFF`"
                    class="inline mx-2 bg-green-50"
                  />
                  <span
                    class="cursor-pointer fas fa-question-circle text-secondary-40"
                    v-tooltip="promotionOnCancelTooltip"
                  />
                </span>
              </td>
              <td class="py-2 table-plan-value" v-else>
                {{ amountBillingCycle | formatNumber }} {{ getCurrency }}
                <p v-if="$store.state.plan.oldPlan" class="text-xs">
                  {{ $i18nPresenter.t(`Although the price for this plan has increased after you subscribed, you are still paying the old price for as long as your subscription goes.`) }}
                </p>
              </td>
            </tr>
          </tbody>
        </table>
        <AhaInfoBox
          class="inline-block mb-3"
          v-if="isValidPromoCode"
        >
          <span class="text-sm text-secondary-100">
            {{ $i18nPresenter.t('Click Upgrade today and save on any account plan! Don’t miss out—offer ends soon!') }}
          </span>
        </AhaInfoBox>
        <div class="flex">
          <aha-antd-button
            v-aha-emit-action
            name="plan-upgrade-from-subscription"
            class="mr-3 px-3 mb-1"
            @click="scrollToPlanUpgradePlan"
          >
            {{ $i18nPresenter.t('Upgrade') }}
          </aha-antd-button>
          <aha-antd-button
            v-if="hasNextBillingDate"
            v-aha-emit-action
            name="plan-cancel"
            variant="secondary"
            class="px-3 mb-1"
            :submitting="isCancelSubscriptionProcessing && !isCancelDataLoaded"
            is-loading-inline
            :disabled="isCancelSubscriptionProcessing && !isCancelDataLoaded"
            @click="handleClickOnCancelSub()"
          >
            {{ $i18nPresenter.t('cancel_subscription') }}
          </aha-antd-button>
        </div>
        <hr class="mb-7 mt-7 text-base-30" />
        <h3 class="font-bold text-lg mb-2">
          {{ $i18nPresenter.t('Billing & Payment') }}
        </h3>
        <table class="table-plan mb-2">
          <colgroup>
            <col class="w-1/2 md:w-1/4">
            <col class="w-1/2 md:w-3/4">
          </colgroup>
          <tbody class="w-full">
            <tr v-if="isShowTurnOnRenewal">
              <td class="table-plan-property">
                <i class="mr-1 fas fa-undo-alt fa-fw" />
                {{ $i18nPresenter.t('Auto-renewal:') }}
              </td>
              <td class="py-2 table-plan-value">
                <span>
                  {{ $i18nPresenter.t('Off') }} <span v-if="!autoRenewOn && !isIDealSubscription">
                    (<a
                      class="autorenewing-text"
                      v-if="isTurningOnAutoRenew"
                    >{{ $i18nPresenter.t('turning on...') }}</a>
                    <a
                      class="text-link--muted"
                      v-else
                      @click="$emit('handleTurnOnAutoRenew')"
                    >{{ $i18nPresenter.t('turn on') }}</a>)
                  </span>
                </span>
              </td>
            </tr>
            <tr v-if="$store.state.currentUser.currency !== 'eur'">
              <td class="table-plan-property">
                <i class="mr-1 far fa-credit-card fa-fw" />
                {{ $i18nPresenter.t('Payment method:') }}
              </td>
              <td class="p-0">
                <div class="col" v-if="!cardInfo">
                  <div>
                    <strong class="uppercase mb-2">
                      {{ cardInfo }}
                    </strong>
                  </div>
                </div>
                <aha-antd-button
                  v-else
                  :submitting="isCreatingCustomerPortal"
                  v-aha-emit-action
                  is-loading-inline
                  name="pay-user-add-card"
                  variant="secondary"
                  @click="redirectToCustomerPortal('add_card')"
                  class="text-link"
                >
                  {{ $i18nPresenter.t('Add') }}
                </aha-antd-button>
              </td>
            </tr>
            <tr>
              <td class="table-plan-property">
                <aha-icon name="coin" class="-mt-0.5 -ml-0.5" />
                {{ $i18nPresenter.t('Credit balance:') }}
              </td>
              <td
                class="py-2 table-plan-value"
              >
                <span v-if="currentUser.balance">
                  {{ (0 - parseFloat(currentUser.balance)) | currencyFormat(getCurrency) }} {{ getCurrency }}
                </span>
                <span v-else>{{ 0 | currencyFormat(getCurrency) }} {{ getCurrency }}</span>
              </td>
            </tr>
            <tr>
              <td class="table-plan-property">
                <i class="mr-1 fas fa-address-card fa-fw" />
                {{ $i18nPresenter.t('Billing info:') }}
              </td>
              <td>
                <div
                  v-if="billingInfo"
                  class="table-plan-property__address mb-2"
                >
                  <span v-if="billingInfo.customerName">
                    <PreLine :text="billingInfo.customerName" /> <br />
                  </span>
                  <span v-if="billingInfo.line1">
                    <PreLine :text="billingInfo.line1" /> <br />
                  </span>
                  <span v-if="billingInfo.line2">
                    <PreLine :text="billingInfo.line2" /> <br />
                  </span>
                  <span v-if="billingInfo.city">
                    <PreLine :text="billingInfo.city" />
                  </span>
                  <span v-if="billingInfo.state">
                    <PreLine :text="`, ${billingInfo.state}`" />
                  </span>
                  <span v-if="billingInfo.postal_code">
                    <PreLine :text="` ${billingInfo.postal_code}`" />
                  </span>

                  <span v-if="billingInfo.country"><PreLine :text="` ${billingInfo.country}`" /></span>

                  <br />
                </div>
                <div v-else>
                  <PreLine :text="currentUser.firstName" /> <br />
                </div>
              </td>
            </tr>
          </tbody>
        </table>
        <aha-antd-button
          v-aha-emit-action
          name="plan-manage"
          class=" mt-2 mb-3"
          variant="secondary"
          :submitting="isCreatingCustomerPortal"
          is-loading-inline
          @click="redirectToCustomerPortal('manager')"
        >
          {{ $i18nPresenter.t('Manage') }}
        </aha-antd-button>
      </template>
    </aha-section>
  </div>
</template>

<script>
import Vue from 'vue'
import { mapActions, mapState } from 'vuex'
import PreLine from '@/components/UI/PreLine.vue'
import utils from '@/util'
import PaymentLocalizationMixin from '@/components-v2/my-plan/user-pricing-v2/PaymentLocalizationMixin'

export default {
  components: { PreLine },
  mixins: [PaymentLocalizationMixin],
  props: {
    data: {
      type: Object,
      default: () => {},
    },
    isDisplayingNextInvoicePromotion: {
      type: Boolean,
      default: false,
    },
    autoRenewOn: {
      type: Boolean,
      default: false,
    },
    isTurningOnAutoRenew: {
      type: Boolean,
      default: false,
    },
    isCancelSubscriptionProcessing: {
      type: Boolean,
      default: false,
    },
    isCancelDataLoaded: {
      type: Boolean,
      default: false,
    },
    isBulkLicenseSubscription: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      isCancelSubInfo: false,
      billings: [],
    }
  },
  computed: {
    ...mapState({
      currentUser: (state) => state.currentUser,
      isTrialPlan: (state) => state.isTrialPlan,
      isCreatingCustomerPortal: (state) => state.isCreatingCustomerPortal,
      plan: (state) => state.plan,
      planBenefit: (state) => state.planBenefit,
      isValidPromoCode: (state) => state.isValidPromoCode,
    }),
    billingInfo() {
      return this.data.address
    },
    isUserSingapore() {
      return this.currentUser.country === 'Singapore'
    },
    isWhiteLabel() {
      return this.$store.state.plan.group === 'white-label'
    },
    isProPlan() {
      return this.$store.state.plan.group == 'pro'
    },
    cardInfo() {
      if (!this.data || !this.data.card) return null
      return `${this.data.card.brand} •••• •••• •••• ${this.data.card.last4}`
    },
    showUpgradePlanFooter() {
      if (this.$store.state.currentUser.accountId
        && this.$store.state.currentUser.accountRole === 'owner'
        && this.$store.state.currentAccount.createdFrom === 'presenter') return true
      return false
    },
    amountBillingCycle() {
      // Zero decimal in stripe, docs : https://stripe.com/docs/currencies#zero-decimal
      return this.data.subscription.amount
    },
    promotionOnCancelAmount() {
      if (!this.data.discount || !this.data.discount.coupon ||
      !this.data.discount.coupon.percent_off || !this.data.subscription) return null
      return Math.round((100 - this.data.discount.coupon.percent_off)
      * this.data.subscription.amount) / 100
    },
    promotionOnCancelPercent() {
      return this.data.discount &&
         this.data.discount.coupon &&
        this.data.discount.coupon.percent_off
    },

    promotionOnCancelTooltip() {
      return this.$i18nPresenter.t('thisExclusiveOfferTakesXPercentOff', {
        percent: this.promotionOnCancelPercent,
      })
    },
    getCurrency() {
      if (this.currentUser.currency === 'eur') return 'EUR'

      return this.fullSymbolCurrency
    },
    isIDealSubscription() {
      if (this.currentUser.currency === 'eur' && this.currentUser.plan !== 'free') return true
      return false
    },
    subscriptionStatus() {
      return this.data
        && this.data.subscription
        && this.data.subscription.status
    },
    periodStart() {
      if (!this.data || !this.data.subscription || !this.data.subscription.current_period_start) return null

      const { metadata } = this.data.subscription

      if (this.subscriptionStatus === 'trialing' && metadata && metadata.extended_period) {
        return Number(metadata.original_start_date) * 1000
      }

      return this.data.subscription.current_period_start
    },
    periodEnd() {
      if (!this.data || !this.data.subscription || !this.data.subscription.current_period_end) return null

      const { metadata } = this.data.subscription

      if (this.subscriptionStatus === 'trialing' && metadata && metadata.updated_next_billing_date) {
        return Number(metadata.updated_next_billing_date) * 1000
      }

      return this.data.subscription.current_period_end
    },
    hasStripeCustomerId() {
      return this.currentUser && this.currentUser.stripeCustomerId
    },
    hasNextBillingDate() {
      if (
        !this.hasStripeCustomerId
        || !this.subscriptionStatus
        || !this.data
        || !this.data.subscription
        || this.data.subscription.cancel_at_period_end
      ) return false

      const { metadata } = this.data.subscription

      if (this.subscriptionStatus === 'trialing' && metadata && metadata.extended_period) return true

      if (
        this.subscriptionStatus !== 'trialing'
        && this.data.subscription
        && !this.data.subscription.cancel_at_period_end
      ) return true

      return false
    },
    isShowTurnOnRenewal() {
      if (this.data.subscription && this.data.subscription.cancel_at_period_end) return true

      return false
    },
    participantLimit() {
      if (!this.data || !this.data.subscription || !this.data.subscription.plan) return null

      const { plan } = this.data.subscription

      return Vue.filter('formatNumber')(plan.limit)
    },
    subscriptionPlanName() {
      if (!this.data || !this.data.subscription || !this.data.subscription.plan) return null

      const { plan } = this.data.subscription
      if (this.isTrialPlan &&
        this.currentUser &&
        this.currentUser.personalInformation &&
        this.currentUser.personalInformation.hasSubscribedTrialPlan
      ) {
        return `Trial ${plan.name}`
      }

      return plan.name
    },
  },
  methods: {
    ...mapActions(['createCustomerPortalAction']),
    getPlanColor(subscription) {
      this.$emit('getPlanColor', subscription)
    },
    handleClickOnCancelSub() {
      this.$emit('cancelSubscription')
    },
    scrollToPlanUpgradePlan() {
      const pricingCompareDom = document.getElementById('upgrade-your-plan')
      pricingCompareDom.scrollIntoView({ behavior: 'smooth' })
    },
    async redirectToCustomerPortal(action) {
      this.createCustomerPortalAction()
      utils.mixpanelTracking('My Plan',
        action === 'add_card' ? 'Billing & Payment > Payment method > Add'
          : 'Billing & Payment > Manage', null, {
          stripeId: this.currentUser.stripeId,
        })
    },
    goToPostLegacy() {
      utils.trackGA4AndMixpanel('Legacy plan tooltips > Learn more', {
        eventAction: 'Legacy plan tooltips > Learn more',
        eventCategory: 'User Plan',
        userId: this.$store.state.currentUser.id,
        plan: this.$store.state.currentUser.plan,
      })
      setTimeout(() => {
        window.open('https://help.ahaslides.com/portal/en/kb/articles/legacy-plan', '_blank')
      }, 500)
    },
    trackingPlusLegacy() {
      utils.trackGA4AndMixpanel('Legacy plan > Hover > Open tooltips', {
        eventAction: 'Legacy plan > Hover > Open tooltips',
        eventCategory: 'User Plan',
        userId: this.$store.state.currentUser.id,
        plan: this.$store.state.currentUser.plan,
      })
    },
  },
}
</script>

<style lang="scss" scoped>
.table-plan {
  @apply w-full;
}
.table-plan-property {
  @apply font-bold py-2 w-1/2 md:w-1/4 align-top;
}
.table-plan-value {
  @apply py-2 w-1/2 md:w-3/4 align-top;
  word-break: break-word;
}

.wrapper-tooltip {
  position: absolute;
  top: 25px;
  left: -50%;
  min-width: 260px;
  padding-top: 9px;
  display: none;
}
.tooltip-legacy-plus {
  background-color: #282E3E;
  color: #fff;
  font-size: 14px;
  font-weight: normal;
  padding: 16px;
  border-radius: 4px;
  line-height: 20px;
}

.cta-upgrade {
  @apply bg-secondary-100 p-3 rounded z-50;
  width: 16rem;
  padding-bottom: 16px;

  div,
  span {
    @apply text-base-0 text-sm;
    font-weight: normal!important;
  }

  span {
    @apply font-normal mt-1;
  }
  .fa-external-link-alt {
    font-size: 12px
  }
}
.bottom-popup {
  top: 3rem;
  left: calc(50% - 150px);
}
.right-popup{
  left: -10px;
  top: 2rem;
  &.upgrade-arrow_slide {
    left: calc(50% - 8px);
  }
}
.center-popup{
  left: -358%;
}
.upgrade-arrow {
  width: 0;
  height: 0;
  border-width: 0 8px 8px 8px;
  border-color: transparent transparent rgba(40, 46, 62, var(--tw-bg-opacity)) transparent;
  border-style: solid;
  position: absolute;
  z-index: 1;
  top: 2.1rem;
  display: none;
  //
  //&.upgrade-arrow_presentation {
  //  left: calc(50% - 47px);
  //}

  right: 10%;
}

.tooltip-audio-upgrade-now {
  .button-upgrade-now {
    margin-top: 12px;
  }
}

.icon-audio-pro {
  &:hover {
    cursor: pointer;
  }

}

.cta-wrapper {
  top: 25px;
  left: calc(50% - 128px);
  position: absolute;
  display: none;
  padding-top: 16px;
}

.legacy-plus-infor {
  cursor: pointer;

  &:hover {
    .cta-wrapper, .upgrade-arrow {
      display: block;
    }
  }

  i {
    color: #525F7F;
    font-size: 18px;
  }
}
</style>
